import { Space, Tag, Typography, theme as antdTheme } from 'antd';
import type { QueryKey } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useMatches } from 'react-router';
import { BackButton } from './back-button';
import { RefreshButton } from './refresh-button';
import { Breadcrumbs, type Crumb } from './breadcrumbs';

export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const TONE_COLOR: Record<StatusTone, string> = {
  success: 'success',
  warning: 'warning',
  danger: 'error',
  info: 'processing',
  neutral: 'default',
};

interface Props {
  eyebrow: string;
  title: string;
  description?: ReactNode;
  count?: { value: number | string; label?: string };
  actions?: ReactNode;

  /**
   * Back-button control:
   *   - omitted   → auto-detected from the route hierarchy (parent route if it exists, else hidden)
   *   - "/path"   → explicit target
   *   - false     → suppress the button (even if a parent exists)
   */
  backTo?: string | false;
  /** Back-button label (default "Back"). */
  backLabel?: string;

  /** Show a refresh button. Called on click; spinner driven by useIsFetching. */
  onRefresh?: () => void;
  /** Query-key filter so the spinner only watches relevant queries. */
  refreshKey?: QueryKey;
  /** Timestamp from `query.dataUpdatedAt` for "Updated 3s ago" tooltip. */
  updatedAt?: number;

  /** Optional per-page breadcrumb override; otherwise the global one in AppHeader handles it. */
  breadcrumbs?: Crumb[];

  /** Status pill rendered next to the title. */
  status?: { tone: StatusTone; label: string };
}

/**
 * Walks `useMatches()` and picks the closest ancestor pathname above the
 * current page that's deeper than '/'. Returns undefined if none qualifies
 * (e.g. on top-level pages like /dashboard or /students list).
 */
function useParentPathname(): string | undefined {
  const matches = useMatches();
  // Last match is the current page; we want the closest ancestor with its own path segment.
  for (let i = matches.length - 2; i >= 0; i--) {
    const p = matches[i].pathname;
    if (p && p !== '/' && p !== matches[matches.length - 1].pathname) return p;
  }
  return undefined;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  count,
  actions,
  backTo,
  backLabel,
  onRefresh,
  refreshKey,
  updatedAt,
  breadcrumbs,
  status,
}: Props) {
  const { token } = antdTheme.useToken();
  const parentPath = useParentPathname();

  // Resolve the back target: explicit string wins; false suppresses; undefined → auto.
  const resolvedBack: string | undefined =
    backTo === false ? undefined : typeof backTo === 'string' ? backTo : parentPath;
  const showBack = resolvedBack != null;

  const showRefresh = onRefresh != null;
  const showBreadcrumbs = breadcrumbs != null && breadcrumbs.length > 0;

  return (
    <header style={{ display: 'grid', gap: 14 }}>
      {showBreadcrumbs && (
        <Space size={6} style={{ fontSize: 12 }}>
          <Breadcrumbs items={breadcrumbs} />
        </Space>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0, flex: '1 1 240px' }}>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }}
          >
            {eyebrow}
          </Typography.Text>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 4,
              flexWrap: 'wrap',
            }}
          >
            {showBack && <BackButton to={resolvedBack} label={backLabel} />}
            <Typography.Title
              level={2}
              className="page-header-title"
              style={{ margin: 0, fontSize: 28, lineHeight: 1.15 }}
            >
              {title}
            </Typography.Title>
            {count != null && (
              <Tag color="processing" style={{ fontSize: 12 }}>
                {count.value}
                {count.label ? ` ${count.label}` : ''}
              </Tag>
            )}
            {status && (
              <Tag color={TONE_COLOR[status.tone]} style={{ fontSize: 12 }}>
                {status.label}
              </Tag>
            )}
            {showRefresh && (
              <RefreshButton
                onRefresh={onRefresh}
                matchKey={refreshKey}
                updatedAt={updatedAt}
              />
            )}
          </div>
          {description && (
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              {description}
            </Typography.Text>
          )}
        </div>
        {actions && (
          <div
            className="page-header-actions"
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
          >
            {actions}
          </div>
        )}
      </div>
      <div style={{ height: 1, background: token.colorBorderSecondary }} />
    </header>
  );
}
